/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFieldArray } from '@jalik/react-form'
import { DateTime } from 'luxon'
import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import { MONTHS } from '../../enums/DurationUnits.mjs'
import {
  MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT,
  MANDATE_EXCLUSIVE_SALE,
  MANDATE_SEASONAL_RENTAL,
  MANDATE_SIMPLE_RENTAL,
  MANDATE_SIMPLE_SALE
} from '../../enums/MandateTypes.mjs'
import {
  calculateAgencyFeesPercentage,
  calculateManagementFeesPercentage
} from '../../lib/common/mandate.mjs'
import { fetchMandateLastNumber, patchMandate, postMandate } from '../../lib/rest/mandateAPIClient'
import MandateSchema from '../../schemas/MandateSchema.mjs'
import AgencyFeesField from '../fields/AgencyFeesField'
import CustomerField from '../fields/CustomerField'
import DurationField from '../fields/DurationField'
import TaxIncludedField from '../fields/TaxIncludedField'
import FormSection from '../FormSection'
import useMandateTypeOptions from './useMandateTypeOptions'
import { useAgencyFeesLabel } from './useAgencyFeesLabel'
import { useLeaseAmountLabel } from './useLeaseAmountLabel'
import { useQuery } from '@tanstack/react-query'
import { randomId } from '../../lib/random.mjs'
import MandateFileField from '../fields/MandateFileField'
import { queryClient } from '../../../../client/js/lib/query'
import EstateInput from '../inputs/EstateInput'
import EstateInfoCard from '../estate/EstateInfoCard'
import { getMandateListLink } from '../../lib/links/mandateLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { ActionIcon, Button, Group, Select, SimpleGrid, Stack } from '@mantine/core'
import PriceField from '../fields/PriceField'
import DurationUnitField from '../fields/DurationUnitField'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'
import { parseNumber } from '@jalik/form-parser'
import FormDateTimeField from '../../../../client/js/components/form/fields/FormDatetimeField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'
import FormField from '../../../../client/js/components/form/fields/FormField'

async function fetchNextMandateNumber () {
  const result = await fetchMandateLastNumber()
  return result.data.number + 1
}

export type MandateFormProps = {
  initialValues: Partial<Mandate>,
  redirectUrl?: string
}

function MandateForm (props: MandateFormProps) {
  const {
    initialValues,
    redirectUrl = getMandateListLink()
  } = props

  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:mandate')

  const form = useCustomForm({
    // disabled: !editable || id != null,
    initialValues: useMemo<Partial<Mandate>>(() => ({
      durationUnit: MONTHS,
      isAgencyFeesWithTax: false,
      isManagementFeesWithTax: false,
      tmpRef: randomId(),
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(MandateSchema),
    validate: createFormValidator(MandateSchema, { removeUnknown: true }),
    validateField: createFieldValidator(MandateSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, MandateSchema)
      return id > 0
        ? patchMandate(id, finalValues)
        : postMandate(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['mandate', id] })
      queryClient.invalidateQueries({ queryKey: ['mandates'] })
    },
    transform (mutation, values) {
      const mut = { ...mutation }

      // Update renewal date.
      if (values.endDate == null &&
        values.duration != null &&
        values.durationUnit != null &&
        values.startDate != null &&
        values.type === MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT) {
        const dt = DateTime.fromISO(values.startDate)
          .plus({ [values.durationUnit]: values.duration })

        const rnDt = DateTime.fromISO(values.renewalDate)
        if (dt.isValid && dt.year >= 1900 && rnDt.toISO() !== dt.toISO()) {
          mut.renewalDate = dt.toISO()
        }
      }

      // Update end date.
      if (values.duration != null &&
        values.durationUnit != null &&
        values.startDate != null &&
        values.type !== MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT) {
        const dt = DateTime.fromISO(values.startDate)
          .plus({ [values.durationUnit]: values.duration })

        if (dt.isValid && dt.year >= 1900) {
          mut.endDate = dt.toISO()
        }
      }
      return mut
    }
  })
  const { setValues, values } = form

  // Calculate agency fees.
  const agencyFeesPercentage = useMemo(() => (
    values.agencyFees != null && values.price != null
      ? calculateAgencyFeesPercentage(values)
      : null
  ), [values])

  // Calculate management fees.
  const managementFeesPercentage = useMemo(() => (
    values.managementFees != null && values.price != null
      ? calculateManagementFeesPercentage(values)
      : null
  ), [values])

  const mandateTypeOptions = useMandateTypeOptions()
  const agencyFeesLabel = useAgencyFeesLabel(values.type, agencyFeesPercentage)
  const amountLabel = useLeaseAmountLabel(values.type)

  const numberQuery = useQuery({
    enabled: form.initialized && id == null,
    queryKey: ['mandateNumber'],
    queryFn: () => fetchNextMandateNumber().then((r) => {
      if (values?.number == null) {
        setValues({ number: r }, { partial: true })
      }
      return r
    }),
    refetchOnWindowFocus: false
  })

  const estatesField = useFieldArray<MandateAndEstate, Partial<Mandate>>({
    name: 'MandateAndEstate',
    defaultValue: {
      mandateId: id,
      estateId: undefined
    },
    context: form
  })

  const estateFilter = useMemo(() => ({
    ownerId: values.customerId,
    excludeId: values.MandateAndEstate?.map((el) => el.estateId)
  }), [values.MandateAndEstate, values.customerId])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? redirectUrl : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <SimpleGrid cols={{ sm: 2 }}>
              <FormField
                label={intl.formatMessage({
                  id: 'type',
                  defaultMessage: 'Type'
                })}
                name="type"
                render={Select}
                data={mandateTypeOptions}
              />
              <FormNumberField
                label={intl.formatMessage({
                  id: 'mandate_number',
                  defaultMessage: 'Numéro du mandat'
                })}
                name="number"
                min={1}
                parser={parseNumber}
                leftSection={(
                  <ActionIcon
                    loading={numberQuery.isFetching}
                    onClick={() => {
                      numberQuery.refetch()
                    }}
                  >
                    <FontAwesomeIcon
                      fixedWidth
                      icon={faRefresh}
                    />
                  </ActionIcon>
                )}
              />
            </SimpleGrid>

            <CustomerField disabled={initialValues?.customerId != null} />

            {initialValues?.id != null
              ? <MandateFileField
                label={intl.formatMessage({
                  id: 'mandate_file',
                  defaultMessage: 'Mandat scanné'
                })}
                mandateId={initialValues?.id}
                // tmpRef={values.tmpRef}
              />
              : null}
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'duration',
              defaultMessage: 'Durée'
            })}
          >
            <SimpleGrid cols={{ sm: 2 }}>
              <DurationField />
              <DurationUnitField />
            </SimpleGrid>

            <SimpleGrid cols={{ sm: 2 }}>
              <FormDateTimeField
                // disabled={item?.startDate != null}
                hideTime
                label={intl.formatMessage({
                  id: 'start_date',
                  defaultMessage: 'Date de début'
                })}
                name="startDate"
              />
              <FormDateTimeField
                disabled={values.type !==
                  MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT}
                hideTime
                label={intl.formatMessage({
                  id: 'end_date',
                  defaultMessage: 'Date de fin'
                })}
                name="endDate"
              />
            </SimpleGrid>

            {values.type === MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT
              ? <SimpleGrid cols={{ sm: 2 }}>
                <FormDateTimeField
                  disabled
                  hideTime
                  label={intl.formatMessage({
                    id: 'renewal_date',
                    defaultMessage: 'Date de renouvellement'
                  })}
                  name="renewalDate"
                />
              </SimpleGrid>
              : null}
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'amounts',
              defaultMessage: 'Montants'
            })}
          >
            <SimpleGrid cols={{ sm: 2 }}>
              <PriceField
                label={amountLabel}
                name="price"
              />
            </SimpleGrid>
            <SimpleGrid cols={{ sm: 2 }}>
              <PriceField
                label={intl.formatMessage({
                  id: 'security_deposit',
                  defaultMessage: 'Dépôt de garantie'
                })}
                name="securityDeposit"
              />
            </SimpleGrid>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'fees',
              defaultMessage: 'Honoraires'
            })}
          >
            <SimpleGrid cols={{ sm: 2 }}>
              <AgencyFeesField
                amount={values.agencyFees}
                disabled={values.type === MANDATE_SEASONAL_RENTAL}
                total={values.price}
                label={agencyFeesLabel}
                name="agencyFees"
              />
              <TaxIncludedField
                disabled={values.type === MANDATE_SEASONAL_RENTAL}
                name="isAgencyFeesWithTax"
              />
            </SimpleGrid>

            <SimpleGrid cols={{ sm: 2 }}>
              <AgencyFeesField
                disabled={values.type !==
                  MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT}
                amount={values.managementFees}
                total={values.price}
                label={intl.formatMessage({
                  id: 'management_fees',
                  defaultMessage: 'Honoraires de gestion ({percentage}%)'
                }, {
                  percentage: managementFeesPercentage != null
                    ? intl.formatNumber(
                      managementFeesPercentage,
                      { maximumFractionDigits: 4 }
                    )
                    : '?'
                })}
                name="managementFees"
              />
              <TaxIncludedField
                disabled={values.type !== MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT}
                name="isManagementFeesWithTax"
              />
            </SimpleGrid>
          </FormSection>
        </SimpleGrid>

        {[
          MANDATE_SIMPLE_RENTAL,
          MANDATE_EXCLUSIVE_RENTAL_MANAGEMENT,
          MANDATE_SEASONAL_RENTAL,
          MANDATE_EXCLUSIVE_SALE,
          MANDATE_SIMPLE_SALE
        ].includes(values.type)
          ? <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'estates',
              defaultMessage: 'Biens'
            })}
          >
            <SimpleGrid cols={{ sm: 2 }}>
              <EstateInput
                disabled={values.customerId == null}
                queryFilter={estateFilter}
                label={intl.formatMessage({
                  id: 'add_estate',
                  defaultMessage: 'Ajouter un bien'
                })}
                onChange={(estateId: string) => {
                  estatesField.append({
                    estateId: parseInt(estateId, 10),
                    mandateId: id
                  })
                }}
              />
            </SimpleGrid>

            <SimpleGrid cols={{ sm: 2, md: 3, lg: 4, xl: 5 }}>
              {estatesField.items.map(({ value, key }, index) => (
                <EstateInfoCard
                  key={key}
                  estateId={value.estateId}
                >
                  <Group justify="center">
                    <Button
                      mt="auto"
                      size="xs"
                      variant="default"
                      onClick={estatesField.handleRemove(index)}
                    >
                      <FormattedMessage
                        id="delete"
                        defaultMessage="Supprimer"
                      />
                    </Button>
                  </Group>
                </EstateInfoCard>
              ))}
            </SimpleGrid>
          </FormSection>
          : null}

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default MandateForm
